import { t } from '@bia-auction/shared-i18n';
import {
  ALLOWED_IMAGE_TYPES,
  ALLOWED_VIDEO_TYPES,
  type MediaKind,
  type VehicleMedia,
  vehicleMediaSchema,
} from '@bia-auction/shared-schema';
import { Button, ConfirmDialog, toast } from '@bia-auction/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Eye, EyeOff, ImagePlus, Star, Trash2, Upload, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api, ApiError, apiUpload } from '../lib/api';
import { prepareImage } from '../lib/image';
import { isVideoKind } from '../lib/media';
import { MediaImg, MediaVideo } from './MediaImg';

/**
 * The vehicle gallery editor (M11, redesign-vehicle.md §4).
 *
 * Every file is downscaled in a canvas and uploaded **individually** (ADR 0025 — no
 * server-side image processing): one rejected file must not fail the whole batch, and a
 * per-file result is the only way an operator can tell which of twelve photos did not land.
 *
 * The cover is a server-side invariant (promoting one demotes the rest in the same batch), so
 * the UI just refetches rather than trying to keep two stars in sync locally.
 */
export function MediaManager({
  vehicleId,
  media,
  disabled,
}: {
  vehicleId: string;
  media: VehicleMedia[];
  disabled?: boolean;
}) {
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<MediaKind>('photo');
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState<VehicleMedia | null>(null);
  // Index into `media` of the item shown full-size, or null for "viewer closed".
  const [viewing, setViewing] = useState<number | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ['vehicle', vehicleId] });

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api(`/admin/vehicles/${vehicleId}/media/${id}`, { method: 'PATCH', body }, vehicleMediaSchema),
    onSuccess: () => void refresh(),
    onError: (e) => toast(e instanceof ApiError ? e.message : t('media.updateFailed'), 'error'),
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      api<void>(`/admin/vehicles/${vehicleId}/media/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setToDelete(null);
      void refresh();
    },
    onError: (e) => {
      setToDelete(null);
      toast(e instanceof ApiError ? e.message : t('media.deleteFailed'), 'error');
    },
  });

  /** Upload files one at a time, reporting each failure by name. */
  const uploadAll = async (files: File[]) => {
    if (files.length === 0) return;
    setBusy(true);
    const video = isVideoKind(kind);
    let ok = 0;
    for (const file of files) {
      try {
        // A video is uploaded as-is: there is nothing to downscale, and `prepareImage` would
        // reject it outright (`createImageBitmap` cannot decode an mp4). Its type is checked
        // here rather than after the upload, for the same reason a photo's is (OQ-4: the
        // admin manager accepts video; the server only takes mp4/webm).
        const prepared = video
          ? { blob: file, contentType: file.type, width: null, height: null }
          : await prepareImage(file);
        if (video && !(ALLOWED_VIDEO_TYPES as readonly string[]).includes(prepared.contentType)) {
          throw new Error('unsupported-video');
        }
        const size =
          prepared.width && prepared.height
            ? `&width=${prepared.width}&height=${prepared.height}`
            : '';
        await apiUpload(
          `/admin/vehicles/${vehicleId}/media?kind=${kind}${size}`,
          prepared.blob,
          prepared.contentType,
          vehicleMediaSchema,
        );
        ok += 1;
      } catch (e) {
        const reason =
          e instanceof ApiError
            ? e.message
            : e instanceof Error && e.message === 'decode-failed'
              ? t('media.error.decode')
              : e instanceof Error && e.message === 'too-large-unencodable'
                ? t('media.error.unencodable')
                : e instanceof Error && e.message === 'unsupported-video'
                  ? t('media.error.videoType')
                  : t('media.error.generic');
        toast(`${file.name}: ${reason}`, 'error');
      }
    }
    setBusy(false);
    if (ok > 0) {
      toast(t('media.uploaded', { n: String(ok) }), 'success');
      void refresh();
    }
  };

  /** Swap two entries' `sortIndex` — the smallest write that expresses "move left/right". */
  const move = (index: number, delta: number) => {
    const a = media[index];
    const b = media[index + delta];
    if (!a || !b) return;
    patch.mutate({ id: a.id, body: { sortIndex: b.sortIndex } });
    patch.mutate({ id: b.id, body: { sortIndex: a.sortIndex } });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-sm">
          <span className="sr-only">{t('media.kind')}</span>
          <select
            className="h-9 rounded-md border border-input bg-surface px-3 text-sm"
            value={kind}
            disabled={disabled || busy}
            onChange={(e) => setKind(e.target.value as MediaKind)}
          >
            <option value="photo">{t('media.kind.photo')}</option>
            <option value="damage_photo">{t('media.kind.damagePhoto')}</option>
            <option value="video">{t('media.kind.video')}</option>
            <option value="damage_video">{t('media.kind.damageVideo')}</option>
          </select>
        </label>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled || busy}
          onClick={() => fileInput.current?.click()}
        >
          <ImagePlus className="h-4 w-4" /> {t('media.add')}
        </Button>
        {busy && <span className="text-sm text-muted-foreground">{t('media.uploading')}</span>}
      </div>

      {/* The picker offers what the selected kind can actually store — an mp4 chosen under
          "Fotoğraf" would only 415 after the upload. */}
      <input
        ref={fileInput}
        type="file"
        accept={(isVideoKind(kind) ? ALLOWED_VIDEO_TYPES : ALLOWED_IMAGE_TYPES).join(',')}
        multiple
        className="sr-only"
        onChange={(e) => {
          void uploadAll([...(e.target.files ?? [])]);
          e.target.value = ''; // re-selecting the same file must re-fire `change`
        }}
      />

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (!disabled && !busy) void uploadAll([...e.dataTransfer.files]);
        }}
        className={`rounded-lg border-2 border-dashed p-4 transition ${
          dragging ? 'border-tx-brand bg-muted' : 'border-border'
        }`}
      >
        {media.length === 0 ? (
          <p className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
            <Upload aria-hidden className="h-4 w-4" />
            {t('media.dropHint')}
          </p>
        ) : (
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {media.map((m, i) => (
              <li key={m.id} className="overflow-hidden rounded-md border">
                <div className="relative aspect-[4/3] bg-muted">
                  {isVideoKind(m.kind) ? (
                    // A video already has its own controls; wrapping it in a button would
                    // swallow play/scrub clicks. Only stills open the viewer.
                    <MediaVideo path={m.url} controls className="h-full w-full object-cover" />
                  ) : (
                    <button
                      type="button"
                      className="block h-full w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label={t('media.open')}
                      onClick={() => setViewing(i)}
                    >
                      <MediaImg
                        path={m.url}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    </button>
                  )}
                  {m.isCover && (
                    <span className="absolute left-1 top-1 rounded bg-primary px-1.5 py-0.5 text-xs font-semibold text-primary-foreground">
                      {t('media.cover')}
                    </span>
                  )}
                  {!m.showPublic && (
                    <span className="absolute right-1 top-1 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                      {t('media.membersOnly')}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between gap-1 p-1">
                  <div className="flex">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={t('media.moveLeft')}
                      disabled={disabled || i === 0}
                      onClick={() => move(i, -1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={t('media.moveRight')}
                      disabled={disabled || i === media.length - 1}
                      onClick={() => move(i, 1)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={t('media.makeCover')}
                      aria-pressed={m.isCover}
                      disabled={disabled || m.isCover}
                      onClick={() => patch.mutate({ id: m.id, body: { isCover: true } })}
                    >
                      <Star className={`h-4 w-4 ${m.isCover ? 'fill-current' : ''}`} />
                    </Button>
                    {/* The control that decides whether a logged-out visitor sees this photo
                        at all — it gets a real label, not just an icon. */}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={m.showPublic ? t('media.hideFromPublic') : t('media.showToPublic')}
                      aria-pressed={m.showPublic}
                      disabled={disabled}
                      onClick={() => patch.mutate({ id: m.id, body: { showPublic: !m.showPublic } })}
                    >
                      {m.showPublic ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={t('common.delete')}
                      disabled={disabled}
                      onClick={() => setToDelete(m)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {viewing !== null && media[viewing] && (
        <MediaViewer media={media} index={viewing} onIndexChange={setViewing} onClose={() => setViewing(null)} />
      )}

      {toDelete && (
        <ConfirmDialog
          open
          busy={remove.isPending}
          title={t('media.deleteTitle')}
          description={t('media.deleteBody')}
          confirmLabel={t('common.delete')}
          cancelLabel={t('common.cancel')}
          destructive
          onConfirm={() => remove.mutate(toDelete.id)}
          onOpenChange={(open) => !open && setToDelete(null)}
        />
      )}
    </div>
  );
}

/**
 * Full-size viewer for the manager's grid (Bia feedback, 07.08.2026 — "araç fotosu büyük de
 * görüntülenebilmeli fotoya tıklayınca"). The grid crops to `object-cover` for an even layout,
 * which is exactly wrong for judging a photo, so the viewer is `object-contain`: an operator
 * checking whether a dent is in frame has to see the whole frame.
 *
 * Deliberately local rather than shared with the bidder's `Gallery`: that one is a browsing
 * carousel with a thumbnail strip, this is a single-image inspector opened from a grid that
 * already *is* the strip. Escape closes and the arrows step, so a keyboard user is never
 * trapped behind a fixed overlay.
 */
function MediaViewer({
  media,
  index,
  onIndexChange,
  onClose,
}: {
  media: VehicleMedia[];
  index: number;
  onIndexChange: (next: number) => void;
  onClose: () => void;
}) {
  const count = media.length;
  const current = media[index]!;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onIndexChange((index + 1) % count);
      if (e.key === 'ArrowLeft') onIndexChange((index - 1 + count) % count);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, count, onIndexChange, onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('media.open')}
      // `bg-overlay`, not a raw navy ramp step: `--overlay` is the design system's modal
      // scrim (docs/15 §4) and the one `packages/ui`'s own Dialog uses, so this viewer dims
      // the page exactly as every other modal does and follows the theme in both directions.
      className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4"
      onClick={onClose}
    >
      {isVideoKind(current.kind) ? (
        <MediaVideo
          path={current.url}
          controls
          autoPlay
          className="max-h-full max-w-full"
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <MediaImg
          path={current.url}
          alt=""
          className="max-h-full max-w-full object-contain"
          onClick={(e) => e.stopPropagation()}
        />
      )}
      {count > 1 && (
        <>
          <ViewerArrow
            side="left"
            onClick={() => onIndexChange((index - 1 + count) % count)}
          />
          <ViewerArrow side="right" onClick={() => onIndexChange((index + 1) % count)} />
          {/* The chrome (counter + arrows) stays a fixed navy ramp step rather than a semantic
              surface token: it sits on the photo, not on the page, so it must read as dark in
              BOTH themes — `bg-surface` would flip to white over the image in light mode. */}
          <span className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded bg-navy-800/80 px-2 py-0.5 text-xs text-white tabular-nums">
            {t('media.viewerCounter', { i: String(index + 1), n: String(count) })}
          </span>
        </>
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={t('common.close')}
        className="absolute right-3 top-3 text-white"
        onClick={onClose}
      >
        <X className="h-5 w-5" />
      </Button>
    </div>
  );
}

function ViewerArrow({ side, onClick }: { side: 'left' | 'right'; onClick: () => void }) {
  const Icon = side === 'left' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      aria-label={side === 'left' ? t('media.moveLeft') : t('media.moveRight')}
      onClick={(e) => {
        e.stopPropagation(); // the backdrop closes on click; stepping must not also close
        onClick();
      }}
      className={`absolute top-1/2 -translate-y-1/2 rounded-full bg-navy-800/70 p-2 text-white hover:bg-navy-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        side === 'left' ? 'left-3' : 'right-3'
      }`}
    >
      <Icon className="h-5 w-5" />
    </button>
  );
}
